import Error from '@components/error';
import { showNotification } from '@helpers/notification-helper';
import { useAppSelector } from '@hooks/app/redux';
import {
  sendAnnouncementEmail,
  sendAnnouncementPush,
  sendAnnouncementSms,
} from '@services/external/announcements.api';
import { Button, Drawer, SegmentedControl, TextField } from '@ui';
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import AlertFormPreview from './alert-form-preview';
import AlertSuccess from './alert-success';
import { AlertsFormSC } from './alerts-form.styled';

type TProps = {
  fetchHistory(): void;
  visible: boolean;
  onClose: () => void;
};

const TAB_OPTIONS = [
  {
    value: 'HILBI',
    label: 'alerts.form.tab_hilbi',
    key: 'alerts.form.tab_hilbi',
  },
  {
    value: 'SMS',
    label: 'alerts.form.tab_sms',
    key: 'alerts.form.tab_sms',
  },
  {
    value: 'EMAIL',
    label: 'alerts.form.tab_email',
    key: 'alerts.form.tab_email',
  },
] as const;

const AlertsForm: React.FC<TProps> = ({ fetchHistory, onClose, visible }) => {
  const { formatMessage } = useIntl();

  const [method, setMethod] = useState<'HILBI' | 'SMS' | 'EMAIL'>('HILBI');
  const [text, setText] = useState('');
  const [step, setStep] = useState<'form' | 'preview' | 'status'>('status');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const currentUser = useAppSelector((store) => store.users.currentUser);
  const providerId = useAppSelector((state) => state.users.currentProvider?.id)!;
  const tabOptions = React.useMemo(
    () => TAB_OPTIONS.map((o) => ({ ...o, label: formatMessage({ id: o.label }) })),
    [],
  );

  React.useEffect(() => {
    setMethod('HILBI');
    setStep('form');
    setText('');
    setError('');
    setLoading(false);
  }, [visible]);

  const getDoctorName = () => {
    if (currentUser) {
      const {
        profile: { firstName, lastName, titleBeforeName, titleAfterName },
      } = currentUser;
      return `${titleBeforeName || ''} ${firstName} ${lastName} ${titleAfterName || ''}`.trim();
    }
    return '';
  };

  const onSubmit = (e: any) => {
    e.preventDefault();
    setError('');

    if (text === '') {
      setError('validation.required');
    } else {
      setStep('preview');
    }
  };

  const onSuccess = () => {
    fetchHistory();

    setTimeout(() => {
      setLoading(false);
      setStep('status');
      setText('');
    }, 300);
  };

  const onError = (err: any) => {
    setLoading(false);
    setStep('preview');

    showNotification({
      type: 'ERROR',
      title: <FormattedMessage id="server_error.error_occurred" />,
      copy: <FormattedMessage id="server_error.something_wrong" />,
    });
  };

  const onConfirm = () => {
    if (method === 'SMS') {
      setLoading(true);
      sendAnnouncementSms(providerId, {
        body: text + `\n${getDoctorName()}`,
        dateToSend: null,
      })
        .then(onSuccess)
        .catch(onError);
    }
    if (method === 'EMAIL') {
      setLoading(true);
      sendAnnouncementEmail(providerId, {
        body: text,
        dateToSend: null,
      })
        .then(onSuccess)
        .catch(onError);
    }
    if (method === 'HILBI') {
      setLoading(true);
      sendAnnouncementPush(providerId, {
        body: text,
      })
        .then(onSuccess)
        .catch(onError);
    }
  };

  const onTextChange = (e: any) => {
    const v = e.currentTarget.value;
    setText(v);
    if (v !== '') {
      setError('');
    }
  };

  return (
    <Drawer visible={visible} title={'Vytvorenie oznamu'} onClose={onClose} destroyOnClose>
      <AlertsFormSC>
        {step === 'form' && (
          <>
            <Drawer.Description>
              <FormattedMessage id="alerts.form.title" />
              &nbsp;
              <span style={{ fontWeight: 'bold' }}>
                <FormattedMessage id="alerts.form.title_2" />
              </span>
            </Drawer.Description>
            <form onSubmit={onSubmit}>
              <SegmentedControl value={method} options={tabOptions} onChange={setMethod} />
              <TextField.TextArea
                value={text}
                onChange={onTextChange}
                placeholder={formatMessage({ id: 'alerts.form.textarea_placeholder' })}
                style={{ width: '100%', marginTop: 16 }}
                autoSize={{ minRows: 3, maxRows: 10 }}
                error={!!error}
              />
              <Error message={error} />
              <div className="af-bottom">
                <Button type="submit" rightArrow>
                  Skontrolovať
                </Button>
              </div>
            </form>
          </>
        )}
        {step === 'preview' && (
          <AlertFormPreview
            onBack={() => setStep('form')}
            text={text}
            method={method}
            onConfirm={onConfirm}
            loading={loading}
            doctorName={`${getDoctorName()}`}
          />
        )}
        {step === 'status' && (
          <AlertSuccess
            onClose={() => {
              fetchHistory();
              onClose();
            }}
          />
        )}
      </AlertsFormSC>
    </Drawer>
  );
};

export default AlertsForm;
